// /src/pages/Solutions.jsx
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaUniversity, FaHospital, FaGraduationCap, FaUsers, 
  FaRobot, FaCloud, FaExchangeAlt, FaMobile, 
  FaArrowRight, FaBuilding, FaDatabase, FaIndustry
} from 'react-icons/fa';
import { productsData, getCategoryIcon } from '@data/productsData';

// Get unique categories from products
const getCategories = () => {
  const categories = {};
  productsData.forEach(product => {
    if (!categories[product.category]) {
      categories[product.category] = {
        name: product.category,
        count: 0,
        products: []
      };
    }
    categories[product.category].count++;
    categories[product.category].products.push(product);
  });
  return categories;
};

// Category icons mapping
const categoryIcons = {
  'Core Banking': FaUniversity,
  'ERP': FaCloud,
  'Payments': FaExchangeAlt,
  'Healthcare': FaHospital,
  'Education': FaGraduationCap,
  'HR': FaUsers,
  'Mobile': FaMobile,
  'Communication': FaDatabase,
  'AI': FaRobot,
  'Custom': FaIndustry,
  'Integration': FaBuilding
};

// Category descriptions
const categoryDescriptions = {
  'Core Banking': 'Core banking & SACCO management solutions',
  'ERP': 'Enterprise resource planning systems',
  'Payments': 'Payment integration & switching platforms',
  'Healthcare': 'Hospital management & patient care systems',
  'Education': 'School & learning management systems',
  'HR': 'Human resource & payroll management',
  'Mobile': 'Mobile banking & payment solutions',
  'Communication': 'Bulk SMS & communication platforms',
  'AI': 'AI solutions & training programs',
  'Custom': 'Tailored software development',
  'Integration': 'API & system integration services'
};

// Category gradients
const categoryGradients = {
  'Core Banking': 'from-amber-500 to-orange-500',
  'ERP': 'from-blue-500 to-indigo-500',
  'Payments': 'from-emerald-500 to-teal-500',
  'Healthcare': 'from-red-500 to-rose-500',
  'Education': 'from-sky-500 to-blue-500',
  'HR': 'from-purple-500 to-pink-500',
  'Mobile': 'from-green-500 to-emerald-500',
  'Communication': 'from-yellow-500 to-amber-500',
  'AI': 'from-fuchsia-500 to-pink-500',
  'Custom': 'from-gray-500 to-gray-600',
  'Integration': 'from-indigo-500 to-purple-500'
};

export default function SolutionsPage() {
  const categories = getCategories();
  const categoryKeys = Object.keys(categories);

  return (
    <div className="py-16 md:py-20 bg-gray-50">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-10 md:mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-micropoint-gold/10 text-micropoint-gold px-4 py-1 rounded-full text-sm font-semibold mb-3">
              Industry Solutions
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 font-display">
              Solutions by <span className="text-micropoint-gold">Industry</span>
            </h1>
            <div className="w-20 h-1 bg-gradient-to-r from-micropoint-gold to-yellow-500 mx-auto mt-4 rounded-full" />
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-sm md:text-base">
              Explore our comprehensive solutions across different industries and sectors
            </p>
          </motion.div>
        </div>

        {/* Categories Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
          {categoryKeys.map((key, index) => {
            const category = categories[key];
            const Icon = categoryIcons[key] || FaBuilding;
            const gradient = categoryGradients[key] || 'from-gray-500 to-gray-600';
            const description = categoryDescriptions[key] || 'Comprehensive solutions';
            const emojiIcon = getCategoryIcon(key);

            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="group"
              >
                <Link to={`/products?category=${encodeURIComponent(key)}`}>
                  <div className="bg-white rounded-2xl border border-gray-200 hover:border-micropoint-gold hover:shadow-xl transition-all duration-300 h-full p-6 flex flex-col items-center text-center relative overflow-hidden">
                    {/* Gradient bar */}
                    <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${gradient}`} />
                    
                    {/* Decorative circle */}
                    <div className="absolute -top-24 -right-24 w-48 h-48 bg-micropoint-gold/5 rounded-full group-hover:scale-150 transition-all duration-500" />
                    
                    <div className="relative z-10 pt-2 flex-1 flex flex-col items-center">
                      {/* Icon */}
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                        <Icon className="text-3xl text-white" />
                      </div>
                      
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1 group-hover:text-micropoint-gold transition-colors">
                        {key}
                      </h3>
                      
                      <p className="text-gray-500 text-sm mb-3">
                        {description}
                      </p>
                      
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                          {category.count} Products
                        </span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                          {emojiIcon}
                        </span>
                      </div>
                      
                      <div className="mt-auto text-micropoint-gold text-sm font-medium group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                        View Products <span className="text-xs">→</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-12 bg-white rounded-2xl p-8 border border-gray-200 text-center shadow-sm relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-micropoint-gold/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative z-10">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 font-display">
              Need a <span className="text-micropoint-gold">Custom Solution</span>?
            </h3>
            <p className="text-gray-600 text-sm md:text-base mb-5 max-w-lg mx-auto">
              We design and build tailored software solutions to meet your unique business requirements.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-micropoint-gold to-yellow-500 hover:from-yellow-500 hover:to-micropoint-gold text-white px-6 md:px-8 py-2.5 md:py-3 rounded-full font-semibold transition-all hover:scale-105 hover:shadow-lg text-sm md:text-base"
            >
              <span>Contact Us</span>
              <FaArrowRight className="text-xs" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}